import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import React from "react";

import type { TransactionDetails } from "@/api/wallet-query";
import type {
	ColumnFiltersState,
	SortingState,
	VisibilityState
} from "@tanstack/react-table";

import { useTransactions } from "@/api/wallet-query";
import { dateRangeFilter, fuzzyFilter } from "@/components/data-table/filters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { cn, formatCurrency } from "@/lib/utils";
import { Route as RootRoute } from "@/routes/__root";
import { useRouter, useSearch } from "@tanstack/react-router";
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	useReactTable
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<TransactionDetails>();

const columns = [
	columnHelper.display({
		id: "details",
		cell: ({ row }) => (
			<div className="flex items-center  gap-3">
				<div className={cn("p-1 rounded-lg bg-red-50", { "bg-green-50": row.original.type === "DEPOSIT" })}>
					{row.original.type === "DEPOSIT"
						? (
								<ArrowDownLeft className="w-5 h-5 text-green-600" />
							)
						: (
								<ArrowUpRight className="w-5 h-5 text-red-600" />
							)}
				</div>
				<div className="space-y-1">
					<h3 className="font-medium text-base capitalize">{row.original.type.toLowerCase()}</h3>
					<p className="text-sm text-gray-500">{row.original.refId}</p>
					<p className="text-sm text-gray-500">{new Date(row.original.createdAt).toLocaleString()}</p>
				</div>
			</div>
		)
	}),
	columnHelper.display({
		id: "amount",
		cell: ({ row }) => (
			<div className="flex flex-col items-end gap-2">
				<span className="font-medium">
					{ formatCurrency(Number(row.original.amount), "NGN")}
				</span>
				<span
					className={cn(
						"rounded-xl px-1.5 py-1 text-xs capitalize leading-none no-underline group-hover:no-underline",
						{
							"bg-green-100 text-green-700": row.original.status === "COMPLETED",
							"bg-yellow-100 text-yellow-700": row.original.status === "PENDING",
							"bg-red-100 text-red-700": row.original.status === "FAILED"
						}
					)}
				>
					{row.original.status.toLowerCase()}
				</span>
			</div>
		)
	})
];

export default function TransactionTable() {
	const router = useRouter();
	const search = useSearch({ from: RootRoute.id });
	const [pageIndex, setPageIndex] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(5);
	const [sorting] = React.useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(() => {
		// Initialize filters from URL params
		const filters: ColumnFiltersState = [];
		Object.entries(search).forEach(([key, value]) => {
			if (key.startsWith("filter_")) {
				const id = key.replace("filter_", "");
				filters.push({ id, value: String(value) });
			}
		});
		return filters;
	});
	const [columnVisibility] = React.useState<VisibilityState>({});
	const [rowSelection] = React.useState({});
	const { data: transactions, isPending, isError } = useTransactions();

	const table = useReactTable({
		data: transactions?.data.items ?? [],
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		filterFns: {
			fuzzy: fuzzyFilter,
			dateRange: dateRangeFilter
		},
		globalFilterFn: fuzzyFilter,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			pagination: {
				pageIndex,
				pageSize
			}
		},
		onColumnFiltersChange: (updater) => {
			if (typeof updater === "function") {
				const newFilters = updater(columnFilters);
				setColumnFilters(newFilters);

				// Update URL params with new filters
				const newSearch = { ...search } as Record<string, string>;
				newFilters.forEach((filter) => {
					if (filter.value) {
						newSearch[`${filter.id}`] = String(filter.value);
					}
					else {
						delete newSearch[`${filter.id}`];
					}
				});
				router.navigate({ to: "/vendor/contract", search: newSearch });
			}
		},
		onPaginationChange: (updater) => {
			if (typeof updater === "function") {
				const newState = updater({
					pageIndex,
					pageSize
				});
				setPageIndex(newState.pageIndex);
				setPageSize(newState.pageSize);
			}
		}
	});

	return (
		<Card>
			<CardHeader className="flex flex-wrap flex-col md:flex-row gap-4 md:items-center justify-between">
				<h2 className="text-xl font-semibold">Wallet history</h2>
			</CardHeader>
			<CardContent>
				<div className="w-full max-w-4xl mx-auto flex flex-col min-h-[calc(100vh-10rem)] ">
					<div className="flex-grow overflow-auto">
						<Table className="w-full border-separate border-spacing-y-4">
							<TableBody>
								{isError
									? (
											<TableRow>
												<TableCell colSpan={columns.length} className="h-96">
													<div className="w-full h-full flex items-center justify-center">
														Something went wrong!
													</div>
												</TableCell>
											</TableRow>
										)
									: isPending
										? (
												<TableRow>
													<TableCell colSpan={columns.length} className="h-96">
														<div className="w-full h-full flex items-center justify-center">
															<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
														</div>
													</TableCell>
												</TableRow>
											)
										: table.getRowModel().rows?.length
											? (
													table.getRowModel().rows.map(row => (
														<TableRow key={row.id} className="bg-white shadow-sm rounded-lg">
															{row.getVisibleCells().map(cell => (
																<TableCell
																	key={cell.id}
																	className={`p-3 ${
																		cell.column.id === "details"
																			? "rounded-l-lg border-y border-l"
																			: "rounded-r-lg border-y border-r text-right"
																	}`}
																>
																	{flexRender(cell.column.columnDef.cell, cell.getContext())}
																</TableCell>
															))}
														</TableRow>
													))
												)
											: (
													<TableRow>
														<TableCell colSpan={columns.length} className="h-24 text-center overflow-hidden">
															<div className="flex flex-col items-center justify-center gap-4 my-auto min-h-64 h-full mt-36">
																<img src="/assets/no-transaction.svg" alt="" />
																<div>
																	<h4 className="font-bold text-black text-lg">Your recent transactions will appear here</h4>
																	<span className="text-sm text-muted-foreground">Fund your wallet to get started</span>
																</div>
															</div>
														</TableCell>
													</TableRow>
												)}
							</TableBody>
						</Table>
					</div>
					<div className="flex justify-end gap-2 mt-4 pt-4 border-t">
						<Button variant="outline" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
							Previous
						</Button>
						<Button variant="outline" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
							Next
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
